import {useState} from 'react'
import isEqual from 'lodash/isEqual.js'

import OmniBar, {
  parseInputValue,
  processDateFilterValue,
  processIntervalFilterValue,
  summarizeDateFilter,
} from '../../common/components/List/OmniBar.js'

import {
  getWarehouseName,
  warehousesSortedByNameSelector,
} from '../../data/warehouses.js'
import {
  setSearchText,
  transferListQuerySelector,
  setTransferSendingWarehouseIDs,
  removeTransferSendingWarehouseID,
  navigateToTransferListPage,
  setTransferReceivingWarehouseIDs,
  removeTransferReceivingWarehouseID,
  setTransferDateRangeFilters,
  setTimezone,
} from './transferListFunctions.js'
import {getState, useSelector} from '../../store.js'
import {
  TRANSFER_DATE_CREATED,
  TRANSFER_DATE_FILTERS,
  TRANSFER_DATE_UPDATED,
  TRANSFER_DEFAULT_DATE_TYPE,
} from '../../common/constants/Transfers.js'

export const SEARCH_TYPE = 'search'
export const SENDING_WAREHOUSE_TYPE = 'sending_warehouse'
export const RECEIVING_WAREHOUSE_TYPE = 'receiving_warehouse'
export const TIMEZONE_TYPE = 'timezone'

export function transferListOmniBarFilterListSelector(state) {
  const cache = transferListOmniBarFilterListSelector.cache
  const warehouses = warehousesSortedByNameSelector(state)
  const query = transferListQuerySelector(state)

  if (cache && isEqual(warehouses, cache.warehouses) && query === cache.query) {
    return cache.list
  }

  transferListOmniBarFilterListSelector.cache = {
    warehouses,
    query,
  }

  const list = transferFilterList({
    warehouses,
    query,
  })

  transferListOmniBarFilterListSelector.cache.list = list

  return list
}

export function transferFilterList({
  warehouses,
  intervalSummary = true,
  query: {
    sending_warehouse_id = [],
    receiving_warehouse_id = [],
    start_date,
    end_date,
    date_type,
    interval_type,
    interval_amount,
    interval_trunc,
    timezone,
    search,
  },
}) {
  const list = []

  warehouses.forEach((warehouse) => {
    if (sending_warehouse_id.includes(warehouse.id)) {
      list.push({
        type: SENDING_WAREHOUSE_TYPE,
        label: getWarehouseName(warehouse),
        value: `${SENDING_WAREHOUSE_TYPE}:${warehouse.id}`,
        id: warehouse.id,
      })
    }
  })

  warehouses.forEach((warehouse) => {
    if (receiving_warehouse_id.includes(warehouse.id)) {
      list.push({
        type: RECEIVING_WAREHOUSE_TYPE,
        label: getWarehouseName(warehouse),
        value: `${RECEIVING_WAREHOUSE_TYPE}:${warehouse.id}`,
        id: warehouse.id,
      })
    }
  })

  list.push(
    ...summarizeDateFilter({
      dateType: date_type,
      startDate: start_date,
      endDate: end_date,
      intervalType: interval_type,
      intervalAmount: interval_amount,
      intervalTrunc: interval_trunc,
      timezone,
      intervalSummary,
    }),
  )

  if (timezone) {
    list.push({
      type: TIMEZONE_TYPE,
      label: timezone,
      value: timezone,
    })
  }

  if (search) {
    list.push({
      type: SEARCH_TYPE,
      label: search,
      value: search,
    })
  }

  return list
}

function getDateTypeAndRest(filterValue) {
  for (const {regex, type} of [
    {regex: /^created? (.*)$/i, type: TRANSFER_DATE_CREATED},
    {regex: /^updated? (.*)$/i, type: TRANSFER_DATE_UPDATED},
  ]) {
    const match = filterValue.match(regex)

    if (match) {
      return {dateType: type, dateRest: match[1]}
    }
  }

  return {dateType: TRANSFER_DEFAULT_DATE_TYPE, dateRest: filterValue}
}

export const PROCESS_VALUE_FUNCTIONS = {
  processWarehouseFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    const outcome = {stop: false}
    const [, type] =
      filterType.match(/^(sending|receiving).warehouse[s]?$/i) || []

    if (!(type && ['', '='].includes(filterOperator))) {
      return outcome
    }

    outcome.stop = true

    const warehouses = warehousesSortedByNameSelector(getState())

    const matchedWarehouses = warehouses.filter((w) => {
      if (filterOperator === '=') {
        if (w.id === Number(filterValue)) {
          return true
        }
        if (getWarehouseName(w) === filterValue) {
          return true
        }
      } else {
        if (
          `${w.address.name} ${w.address.company} ${w.address.city} ${w.address.state}`.match(
            RegExp(filterValue, 'i'),
          )
        ) {
          return true
        }
      }

      return false
    })

    if (matchedWarehouses.length) {
      if (type === 'receiving') {
        outcome.hash = setTransferReceivingWarehouseIDs([
          ...transferListQuerySelector(getState()).receiving_warehouse_id,
          ...matchedWarehouses.map(({id}) => id),
        ])
      } else {
        outcome.hash = setTransferSendingWarehouseIDs([
          ...transferListQuerySelector(getState()).sending_warehouse_id,
          ...matchedWarehouses.map(({id}) => id),
        ])
      }
    }

    return outcome
  },

  processDateFilterValue(inputValue, filterType, filterOperator, filterValue) {
    return processDateFilterValue(
      inputValue,
      filterType,
      filterOperator,
      filterValue,
      getDateTypeAndRest,
      transferListQuerySelector,
      ({startDate, endDate, dateFilterType, timezone}) =>
        setTransferDateRangeFilters({
          start_date: startDate,
          end_date: endDate,
          date_type: dateFilterType,
          timezone,
        }),
    )
  },

  processIntervalFilterValue(
    inputValue,
    filterType,
    filterOperator,
    filterValue,
  ) {
    return processIntervalFilterValue(
      inputValue,
      filterType,
      filterOperator,
      filterValue,
      getDateTypeAndRest,
      transferListQuerySelector,
      ({
        dateFilterType,
        intervalType,
        intervalAmount,
        intervalTrunc,
        timezone,
      }) =>
        setTransferDateRangeFilters({
          date_type: dateFilterType,
          interval_type: intervalType,
          interval_amount: intervalAmount,
          interval_trunc: intervalTrunc,
          timezone,
        }),
    )
  },

  // Put new functions before this function
  processSearchTextFilterValue(inputValue) {
    return {stop: true, hash: setSearchText(inputValue)}
  },
  // Do not put functions after this function
}

export function processInputValue(inputValue, setInputValue) {
  if (!inputValue) {
    setInputValue('')

    return
  }

  const [, filterType, filterOperator, filterValue] =
    parseInputValue(inputValue)

  let hash
  for (const processFilterValue of Object.values(PROCESS_VALUE_FUNCTIONS)) {
    const outcome = processFilterValue(
      inputValue,
      filterType,
      filterOperator,
      filterValue,
    )

    if (outcome.stop) {
      hash = outcome.hash
      break
    }
  }

  if (hash) {
    setInputValue('')

    return hash
  }

  return
}

export function removeValue(value) {
  if (value.type === SENDING_WAREHOUSE_TYPE) {
    return removeTransferSendingWarehouseID(value.id)
  } else if (value.type === RECEIVING_WAREHOUSE_TYPE) {
    return removeTransferReceivingWarehouseID(value.id)
  } else if (TRANSFER_DATE_FILTERS.includes(value.type)) {
    return setTransferDateRangeFilters({
      date_type: null,
      start_date: null,
      end_date: null,
      interval_type: null,
      interval_amount: null,
      interval_trunc: null,
      timezone: null,
    })
  } else if (value.type === TIMEZONE_TYPE) {
    return setTimezone(null)
  } else if (value.type === SEARCH_TYPE) {
    return setSearchText('')
  }
}

export function clearAll() {
  return navigateToTransferListPage()
}

export function editValue(data, setInputValue) {
  if (data.type === SEARCH_TYPE) {
    setInputValue(data.value)
  } else if (TRANSFER_DATE_FILTERS.includes(data.type)) {
    setInputValue(
      `${data.intervalType ? 'interval' : 'date'}: ${data.type} ${data.value}`,
    )
  }
}

export default function TransferListOmniBar() {
  const [inputValue, setInputValue] = useState('')
  const list = useSelector(transferListOmniBarFilterListSelector)

  return (
    <OmniBar
      placeholder="Enter a Transfer Reference ID..."
      inputValue={inputValue}
      value={list}
      onRemoveValue={(removedValue) => removeValue(removedValue)}
      onClear={() => clearAll()}
      onInputValueChange={(inputValue) => setInputValue(inputValue)}
      onProcessInputValue={(inputValue) =>
        processInputValue(inputValue, setInputValue)
      }
      onEditValue={(data) => editValue(data, setInputValue)}
    />
  )
}
